import { type SEOHandle } from '@nasa-gcn/remix-seo'
import { useState, useCallback, useEffect } from 'react'
import { Link, useLoaderData, useFetcher, type LoaderFunctionArgs, type MetaFunction } from 'react-router'
import { serverOnly$ } from 'vite-env-only/macros'
import { DIFFICULTY_PRESET_META, type DifficultyPresetType } from '#app/services/student-tests/constants'
import {
	loadPublicExamBySlug,
	getExamQuestionsForClient,
	type PublicExamQuestion,
} from '#app/services/student-tests/public-exams.server'
import { prisma } from '#app/utils/db.server'
import { cn } from '#app/utils/misc.tsx'
import { MarketingButton } from '../components/marketing-button'

async function getAllPublicExamSlugs(): Promise<string[]> {
	const exams = await prisma.studentTest.findMany({
		where: { type: 'public', slug: { not: null } },
		select: { slug: true },
	})
	return exams.map(e => e.slug!)
}

export const handle: SEOHandle = {
	getSitemapEntries: serverOnly$(async () => {
		const slugs = await getAllPublicExamSlugs()
		return slugs.map(slug => ({
			route: `/examenes-de-practica/${slug}`,
			priority: 0.8,
			changefreq: 'monthly',
		}))
	}),
}

export async function loader({ params }: LoaderFunctionArgs) {
	const slug = params.slug!
	const exam = await loadPublicExamBySlug(slug)
	if (!exam) {
		throw new Response('Examen no encontrado', { status: 404 })
	}

	// Send client-safe questions (no answers) + full exam for SEO rendering
	return {
		slug: exam.slug,
		title: exam.title,
		description: exam.description,
		topics: exam.topics,
		keywords: exam.keywords,
		questionCount: exam.questionCount,
		difficulty: exam.difficulty,
		style: exam.style,
		// Client-safe: no correctOptionId, no explanation, no citations
		clientQuestions: getExamQuestionsForClient(exam.questions),
		// Full questions for SEO rendering (server-rendered HTML, not in JS bundle)
		seoQuestions: exam.questions,
	}
}

export const meta: MetaFunction<typeof loader> = ({ loaderData }) => {
	if (!loaderData) return [{ title: 'Examen no encontrado | IUS PRO' }]
	return [
		{ title: `${loaderData.title} | Examen Gratis Abogados Costa Rica` },
		{ name: 'description', content: loaderData.description },
		{ name: 'og:title', content: `${loaderData.title} | Examen Gratis` },
		{ name: 'og:description', content: loaderData.description },
		{ name: 'og:image', content: 'https://iuspro.com/img/og-image-exam.webp' },
		{ name: 'keywords', content: loaderData.keywords.join(', ') },
	]
}

type ClientQuestion = ReturnType<typeof getExamQuestionsForClient>[number]

const STORAGE_KEY_PREFIX = 'seo-exam-'

function getStorageKey(slug: string) {
	return `${STORAGE_KEY_PREFIX}${slug}`
}

function loadSavedAnswers(slug: string): Record<string, string> {
	if (typeof window === 'undefined') return {}
	try {
		const raw = localStorage.getItem(getStorageKey(slug))
		if (!raw) return {}
		const parsed = JSON.parse(raw) as any
		return parsed.answers ?? {}
	} catch {
		return {}
	}
}

function saveAnswers(slug: string, answers: Record<string, string>) {
	if (typeof window === 'undefined') return
	try {
		localStorage.setItem(getStorageKey(slug), JSON.stringify({ answers, updatedAt: new Date().toISOString() }))
	} catch {
		// localStorage full or unavailable
	}
}

function DifficultyDots({ difficulty }: { difficulty: number }) {
	return (
		<div className="flex items-center gap-0.5">
			{Array.from({ length: 5 }, (_, i) => (
				<div
					key={i}
					className={cn('h-1 w-3 rounded-full transition-colors', i < difficulty ? 'bg-neutral-800' : 'bg-neutral-200')}
				/>
			))}
		</div>
	)
}

// ── Interactive exam-taking mode ──

function ExamTakingMode({
	slug,
	questions,
	title,
	onFinish,
}: {
	slug: string
	questions: ClientQuestion[]
	title: string
	onFinish: (answers: Record<string, string>) => void
}) {
	const [currentIndex, setCurrentIndex] = useState(0)
	const [answers, setAnswers] = useState<Record<string, string>>(() => loadSavedAnswers(slug))
	const [showNav, setShowNav] = useState(false)

	const currentQuestion = questions[currentIndex]!
	const answeredCount = Object.keys(answers).length
	const progress = (answeredCount / questions.length) * 100

	const handleSelect = useCallback(
		(questionId: string, optionId: string) => {
			setAnswers(prev => {
				const next = { ...prev, [questionId]: optionId }
				saveAnswers(slug, next)
				return next
			})
		},
		[slug],
	)

	const goTo = (index: number) => {
		if (index >= 0 && index < questions.length) {
			setCurrentIndex(index)
			setShowNav(false)
		}
	}

	return (
		<div className="h-[100dvh] overflow-y-auto pb-20 pt-24">
			<div className="mx-auto max-w-3xl px-6">
				{/* Header + progress */}
				<div className="mb-6">
					<div className="flex items-center justify-between">
						<h1 className="text-base font-medium text-neutral-900 sm:text-lg">{title}</h1>
						<button
							onClick={() => onFinish(answers)}
							className="rounded-full bg-neutral-900 px-4 py-1.5 text-sm font-medium text-white transition-colors hover:bg-neutral-800"
						>
							Finalizar
						</button>
					</div>
					<div className="mt-3 flex items-center gap-3">
						<div className="h-1 flex-1 overflow-hidden rounded-full bg-neutral-100">
							<div
								className="h-full rounded-full bg-neutral-900 transition-all duration-500"
								style={{ width: `${progress}%` }}
							/>
						</div>
						<span className="text-xs tabular-nums text-neutral-400">
							{answeredCount}/{questions.length}
						</span>
					</div>
				</div>

				{/* Compact navigation toggle */}
				<div className="mb-6">
					<button
						onClick={() => setShowNav(v => !v)}
						className="flex items-center gap-2 rounded-lg border border-neutral-200 px-3 py-1.5 text-sm text-neutral-600 transition-colors hover:bg-neutral-50"
					>
						<span className="font-medium tabular-nums">
							{currentIndex + 1}/{questions.length}
						</span>
						<span className="text-neutral-400">|</span>
						<span className="text-neutral-400">{answeredCount} respondidas</span>
						<svg
							className={cn('h-3.5 w-3.5 text-neutral-400 transition-transform', showNav && 'rotate-180')}
							fill="none"
							viewBox="0 0 24 24"
							stroke="currentColor"
							strokeWidth={2}
						>
							<path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
						</svg>
					</button>

					{showNav && (
						<div className="mt-2 rounded-xl border border-neutral-200 bg-white p-3 shadow-sm">
							<div className="flex flex-wrap gap-1">
								{questions.map((q, i) => (
									<button
										key={q.id}
										onClick={() => goTo(i)}
										className={cn(
											'flex h-7 w-7 items-center justify-center rounded text-xs font-medium transition-all',
											i === currentIndex
												? 'bg-neutral-900 text-white'
												: answers[q.id]
													? 'bg-neutral-300 text-neutral-700'
													: 'bg-neutral-100 text-neutral-400 hover:bg-neutral-200',
										)}
									>
										{i + 1}
									</button>
								))}
							</div>
						</div>
					)}
				</div>

				{/* Question */}
				<div className="flex min-h-[420px] flex-col">
					<div className="mb-2 flex items-center gap-3">
						<span className="text-xs font-medium uppercase tracking-wider text-neutral-400">
							Pregunta {currentIndex + 1}
						</span>
						<DifficultyDots difficulty={currentQuestion.difficulty} />
					</div>

					<h2 className="mb-8 text-lg leading-relaxed text-neutral-900 sm:text-xl">{currentQuestion.questionText}</h2>

					<div className="flex-1 space-y-2.5">
						{currentQuestion.options.map(option => {
							const isSelected = answers[currentQuestion.id] === option.id
							return (
								<button
									key={option.id}
									onClick={() => handleSelect(currentQuestion.id, option.id)}
									className={cn(
										'flex w-full items-start gap-3 rounded-xl border p-4 text-left transition-all duration-150',
										isSelected
											? 'border-neutral-900 bg-neutral-900/[0.03] ring-1 ring-neutral-900'
											: 'border-neutral-200 hover:border-neutral-300 hover:bg-neutral-50',
									)}
								>
									<span
										className={cn(
											'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold uppercase transition-colors',
											isSelected ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-500',
										)}
									>
										{option.id}
									</span>
									<span className="flex-1 text-sm leading-relaxed text-neutral-700">{option.text}</span>
								</button>
							)
						})}
					</div>

					</div>
			</div>

			{/* Navigation — sticky bottom */}
			<div className="fixed inset-x-0 bottom-0 z-40 border-t border-neutral-200 bg-white/95 backdrop-blur-lg">
			<div className="mx-auto flex max-w-3xl items-center justify-between px-6 py-3 sm:py-4">
				<button
					onClick={() => goTo(currentIndex - 1)}
					disabled={currentIndex === 0}
					className="rounded-full border border-neutral-200 px-5 py-2 text-sm font-medium text-neutral-600 transition-colors hover:bg-neutral-50 disabled:opacity-30"
				>
					&larr; Anterior
				</button>

				{currentIndex === questions.length - 1 ? (
					<button
						onClick={() => onFinish(answers)}
						className="rounded-full bg-neutral-900 px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-neutral-800"
					>
						Finalizar examen
					</button>
				) : (
					<button
						onClick={() => goTo(currentIndex + 1)}
						className="rounded-full bg-neutral-900 px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-neutral-800"
					>
						Siguiente &rarr;
					</button>
				)}
			</div>
			</div>
		</div>
	)
}

// ── Score results view ──

function ScoreResultsView({
	slug,
	score,
	total,
	percentage,
}: {
	slug: string
	score: number
	total: number
	percentage: number
}) {
	const passed = percentage >= 70

	return (
		<div className="h-[100dvh] overflow-y-auto">
			<div className="flex items-center justify-center pb-20 pt-24">
				<div className="mx-auto w-full max-w-md px-6">
					<div className="text-center">
						{/* Score circle */}
						<div className="relative mx-auto mb-8 h-32 w-32">
							<svg className="h-full w-full -rotate-90" viewBox="0 0 100 100">
								<circle cx="50" cy="50" r="42" fill="none" stroke="#f5f5f5" strokeWidth="6" />
								<circle
									cx="50"
									cy="50"
									r="42"
									fill="none"
									stroke={passed ? '#16a34a' : percentage >= 50 ? '#ca8a04' : '#dc2626'}
									strokeWidth="6"
									strokeLinecap="round"
									strokeDasharray={`${(percentage / 100) * 264} 264`}
									className="transition-all duration-1000"
								/>
							</svg>
							<div className="absolute inset-0 flex flex-col items-center justify-center">
								<span className="text-3xl font-bold text-neutral-900">{percentage}%</span>
							</div>
						</div>

						<h2 className="font-instrument-serif text-2xl text-neutral-900">
							{score} de {total} correctas
						</h2>
						<p className="mt-2 text-sm text-neutral-500">{passed ? 'Buen resultado' : 'Sigue practicando'}</p>

						<p className="mx-auto mt-6 max-w-xs text-sm leading-relaxed text-neutral-400">
							¿Quieres ver cuáles respondiste correctamente y las explicaciones detalladas?
						</p>

						<div className="mt-6">
							<MarketingButton className="w-full" asChild>
								<Link to={`/examenes-de-practica/${slug}/resultados`}>Ver resultados detallados</Link>
							</MarketingButton>
							<p className="mt-2 text-xs text-neutral-400">Si no tienes cuenta, se te pedirá crear una gratis.</p>
						</div>

						<div className="mt-8">
							<Link
								to="/examenes-de-practica"
								className="text-sm text-neutral-400 transition-colors hover:text-neutral-700"
							>
								Ver más exámenes &rarr;
							</Link>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

// ── Main page component ──

export default function ExamPage() {
	const data = useLoaderData<typeof loader>()
	const scoreFetcher = useFetcher<{ score: number; total: number; percentage: number }>()

	const [mode, setMode] = useState<'browse' | 'taking' | 'scored'>('browse')
	const [scoreData, setScoreData] = useState<{ score: number; total: number; percentage: number } | null>(null)

	// Watch for score response
	useEffect(() => {
		if (scoreFetcher.data && scoreFetcher.state === 'idle') {
			setScoreData(scoreFetcher.data)
			setMode('scored')
		}
	}, [scoreFetcher.data, scoreFetcher.state])

	const handleFinishExam = useCallback(
		(answers: Record<string, string>) => {
			void scoreFetcher.submit(
				{ slug: data.slug, answers: JSON.stringify(answers) },
				{ method: 'post', action: '/resources/score-seo-exam' },
			)
		},
		[data.slug, scoreFetcher],
	)

	const difficultyLabel = DIFFICULTY_PRESET_META[data.difficulty as DifficultyPresetType]?.label ?? data.difficulty

	// ── Taking mode ──
	if (mode === 'taking') {
		return (
			<ExamTakingMode
				slug={data.slug}
				questions={data.clientQuestions}
				title={data.title}
				onFinish={handleFinishExam}
			/>
		)
	}

	// ── Score results ──
	if (mode === 'scored' && scoreData) {
		return <ScoreResultsView slug={data.slug} {...scoreData} />
	}

	// ── Browse mode (SEO content + sticky CTA) ──
	return (
		<div className="h-[100dvh] overflow-y-auto pb-24 pt-24 sm:pb-36">
			<div className="mx-auto max-w-3xl px-6">
				{/* Breadcrumb */}
				<nav className="mb-8 text-sm text-neutral-400">
					<Link to="/examenes-de-practica" className="transition-colors hover:text-neutral-600">
						Exámenes
					</Link>
					<span className="mx-2 text-neutral-300">/</span>
					<span className="text-neutral-500">{data.title}</span>
				</nav>

				{/* Header */}
				<header className="mb-12">
					<h1 className="font-instrument-serif text-3xl font-normal tracking-tight text-neutral-900 sm:text-4xl lg:text-5xl">
						{data.title}
					</h1>
					<p className="mt-4 max-w-2xl text-base leading-relaxed text-neutral-500 sm:text-lg">{data.description}</p>

					{/* Meta pills */}
					<div className="mt-6 flex flex-wrap items-center gap-2">
						<span className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 px-3 py-1 text-sm text-neutral-600">
							<span className="font-semibold text-neutral-900">{data.questionCount}</span> preguntas
						</span>
						<span className="inline-flex items-center gap-1.5 rounded-full bg-neutral-100 px-3 py-1 text-sm text-neutral-600">
							{difficultyLabel}
						</span>
					</div>

					{/* Topics */}
					<div className="mt-4 flex flex-wrap gap-1.5">
						{data.topics.map(topic => (
							<span
								key={topic}
								className="inline-flex items-center rounded-md border border-neutral-200 bg-white px-2.5 py-0.5 text-xs text-neutral-500"
							>
								{topic}
							</span>
						))}
					</div>
				</header>

				{/* Questions section */}
				<section>
					<div className="mb-6 flex items-center gap-3">
						<h2 className="text-lg font-semibold text-neutral-900">Preguntas</h2>
						<div className="h-px flex-1 bg-neutral-100" />
					</div>

					<div className="space-y-4">
						{data.seoQuestions.map((q: PublicExamQuestion, i: number) => (
							<article key={q.id} className="rounded-xl border border-neutral-200 bg-white p-5 sm:p-6">
								<div className="mb-3 flex items-center gap-3">
									<span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-neutral-100 text-xs font-semibold tabular-nums text-neutral-600">
										{i + 1}
									</span>
									<DifficultyDots difficulty={q.difficulty} />
								</div>

								<h3 className="mb-4 text-[15px] font-medium leading-relaxed text-neutral-900">{q.questionText}</h3>

								<div className="space-y-1.5">
									{q.options.map(opt => (
										<div key={opt.id} className="flex items-start gap-2.5 rounded-lg bg-neutral-50 px-3 py-2.5">
											<span className="mt-px text-xs font-semibold uppercase text-neutral-400">{opt.id})</span>
											<span className="text-sm leading-relaxed text-neutral-600">{opt.text}</span>
										</div>
									))}
								</div>
							</article>
						))}
					</div>
				</section>

				{/* Schema.org structured data */}
				<script
					type="application/ld+json"
					dangerouslySetInnerHTML={{
						__html: JSON.stringify({
							'@context': 'https://schema.org',
							'@type': 'Quiz',
							name: data.title,
							description: data.description,
							educationalLevel: 'Professional',
							about: {
								'@type': 'Thing',
								name: data.topics.join(', '),
							},
							numberOfQuestions: data.questionCount,
						}),
					}}
				/>
			</div>

			{/* Sticky bottom CTA bar — prominent on desktop, compact on mobile */}
			<div className="fixed inset-x-0 bottom-0 z-40 border-t border-neutral-200 bg-white/95 backdrop-blur-lg">
				{/* Mobile: compact single row */}
				<div className="flex items-center justify-between px-4 py-3 sm:hidden">
					<span className="text-sm font-medium text-neutral-700">{data.questionCount} preguntas &middot; Gratis</span>
					<MarketingButton size="sm" onClick={() => setMode('taking')}>
						Tomar examen
					</MarketingButton>
				</div>

				{/* Desktop: prominent banner */}
				<div className="mx-auto hidden max-w-3xl items-center justify-between px-6 py-5 sm:flex">
					<div>
						<p className="text-base font-semibold text-neutral-900">¿Listo para poner a prueba tus conocimientos?</p>
						<p className="mt-1 text-sm text-neutral-500">
							Gratis &middot; {data.questionCount} preguntas &middot; Recibe tu puntaje al instante
						</p>
					</div>
					<MarketingButton size="lg" onClick={() => setMode('taking')}>
						Tomar este examen
					</MarketingButton>
				</div>
			</div>
		</div>
	)
}
